import { useState, useEffect, useCallback, Fragment } from "react";
import {
  CheckSquare, Users, Target, Clock, TrendingUp, Calendar, Phone,
  Download, FileSpreadsheet, Search, AlertTriangle, UserCheck, BarChart3,
  ClipboardList, PhoneCall, Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBusiness } from "@/contexts/BusinessContext";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";
import { Trophy, Medal, Award, Activity, BarChart2, GraduationCap, AlarmClock, Hourglass, CalendarDays, XCircle, ChevronRight, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#F97316"];

const SummaryCard = ({ label, value, icon: Icon, color = "indigo" }) => (
  <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
          <h3 className="text-2xl font-heading font-bold text-slate-900 mt-1">{value}</h3>
        </div>
        <div className={`w-10 h-10 bg-${color}-100 rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DateFilters = ({ filters, setFilters, users, showUserFilter = true, extraFilters }) => {
  const quickFilter = (days) => {
    const end = new Date();
    const start = new Date();
    if (days === 0) { /* today */ }
    else if (days === -1) { start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); }
    else { start.setDate(start.getDate() - days); }
    setFilters(f => ({
      ...f,
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0]
    }));
  };

  return (
    <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-slate-400" />
        <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
      </div>
      <div className="flex gap-1">
        {[
          { label: "Today", days: 0 },
          { label: "Yesterday", days: -1 },
          { label: "7 Days", days: 7 },
          { label: "30 Days", days: 30 },
          { label: "This Month", days: "month" }
        ].map(q => (
          <button key={q.label} onClick={() => {
            if (q.days === "month") {
              const now = new Date();
              setFilters(f => ({ ...f, start_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`, end_date: now.toISOString().split("T")[0] }));
            } else quickFilter(q.days);
          }}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 hover:bg-indigo-100 hover:text-indigo-700 transition-colors">
            {q.label}
          </button>
        ))}
      </div>
      {showUserFilter && (
        <select value={filters.user_id || ""} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
          <option value="">All Users</option>
          {users.map(u => <option key={u.user_id || u._id} value={u.user_id || u._id}>{u.name}</option>)}
        </select>
      )}
      {extraFilters}
    </div>
  );
};

const DataTable = ({ columns, data, emptyMessage = "No records found" }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 15;

  const filtered = data.filter(row =>
    columns.some(col => String(row[col.key] || "").toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 p-4 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 text-sm outline-none bg-transparent" />
          <span className="text-xs text-slate-400">{filtered.length} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                {columns.map(col => (
                  <th key={col.key} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={columns.length} className="text-center py-8 text-slate-400">{emptyMessage}</td></tr>
              ) : paginated.map((row, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-slate-700">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] || "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-100">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50">Previous</button>
            <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50">Next</button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    completed: "bg-green-100 text-green-700", done: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700", to_do: "bg-yellow-100 text-yellow-700",
    in_progress: "bg-blue-100 text-blue-700", review: "bg-purple-100 text-purple-700",
    overdue: "bg-red-100 text-red-700", missed: "bg-red-100 text-red-700",
    present: "bg-green-100 text-green-700", absent: "bg-red-100 text-red-700",
    half_day: "bg-orange-100 text-orange-700", late: "bg-amber-100 text-amber-700",
    new: "bg-blue-100 text-blue-700", converted: "bg-green-100 text-green-700",
    lost: "bg-red-100 text-red-700", won: "bg-green-100 text-green-700",
    read: "bg-slate-100 text-slate-700",
  };
  const s = (status || "").toLowerCase().replace(/ /g, "_");
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || "bg-slate-100 text-slate-600"}`}>{status || "-"}</span>;
};

const MiniChart = ({ title, data, type = "bar" }) => {
  if (!data || data.length === 0) return null;
  return (
    <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading font-bold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          {type === "pie" ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis stroke="#64748B" />
              <Tooltip contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: "0.75rem", color: "#fff" }} />
              <Bar dataKey="value" fill="#6366F1" radius={[6, 6, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ─── Task Report Tab ──────────────────────────────
const TaskReport = ({ users }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", user_id: "", status: "" });

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${BACKEND_URL}/api/reports/tasks?${params}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportData = async (format) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("format", format);
    window.open(`${BACKEND_URL}/api/reports/export/tasks?${params}`, "_blank");
  };

  const statusData = data?.by_status ? Object.entries(data.by_status).map(([name, value]) => ({ name: name.replace(/_/g, " "), value })) : [];
  const priorityData = data?.by_priority ? Object.entries(data.by_priority).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="space-y-4">
      <DateFilters filters={filters} setFilters={setFilters} users={users}
        extraFilters={
          <>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none">
              <option value="">All Status</option>
              <option value="to_do">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="review">Review</option>
              <option value="completed">Completed</option>
            </select>
            <button onClick={() => exportData("xlsx")} className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download className="w-4 h-4" /> Export
            </button>
          </>
        }
      />
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Total" value={data.summary.total} icon={ClipboardList} color="indigo" />
            <SummaryCard label="Completed" value={data.summary.completed} icon={CheckSquare} color="green" />
            <SummaryCard label="In Progress" value={data.summary.in_progress} icon={Clock} color="blue" />
            <SummaryCard label="Pending" value={data.summary.pending} icon={AlertTriangle} color="yellow" />
            <SummaryCard label="Overdue" value={data.summary.overdue} icon={AlertTriangle} color="red" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniChart title="By Status" data={statusData} type="pie" />
            <MiniChart title="By Priority" data={priorityData} />
          </div>
          {data.by_user?.length > 0 && (
            <DataTable columns={[
              { key: "name", label: "User" },
              { key: "total", label: "Total" },
              { key: "completed", label: "Completed" },
              { key: "pending", label: "Pending" },
              { key: "overdue", label: "Overdue", render: v => v > 0 ? <span className="text-red-600 font-semibold">{v}</span> : "0" }
            ]} data={data.by_user} emptyMessage="No user data" />
          )}
          <DataTable columns={[
            { key: "title", label: "Task" },
            { key: "status", label: "Status", render: v => <StatusBadge status={v} /> },
            { key: "priority", label: "Priority", render: v => <StatusBadge status={v} /> },
            { key: "assigned_user_name", label: "Assigned To" },
            { key: "due_date", label: "Due Date", render: v => v ? new Date(v).toLocaleDateString("en-IN") : "-" },
            { key: "created_at", label: "Created", render: v => v ? new Date(v).toLocaleDateString("en-IN") : "-" }
          ]} data={data.tasks || []} />
        </>
      )}
    </div>
  );
};

// ─── Lead Report Tab ──────────────────────────────
const LeadReport = ({ users }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", user_id: "", source: "", stage: "" });

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${BACKEND_URL}/api/reports/leads?${params}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportData = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("format", "xlsx");
    window.open(`${BACKEND_URL}/api/reports/export/leads?${params}`, "_blank");
  };

  const sourceData = data?.by_source ? Object.entries(data.by_source).map(([name, value]) => ({ name, value })) : [];
  const stageData = data?.by_stage ? Object.entries(data.by_stage).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="space-y-4">
      <DateFilters filters={filters} setFilters={setFilters} users={users}
        extraFilters={
          <>
            <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none">
              <option value="">All Sources</option>
              {sourceData.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button onClick={exportData} className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download className="w-4 h-4" /> Export
            </button>
          </>
        }
      />
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Total Leads" value={data.summary.total} icon={Users} color="indigo" />
            <SummaryCard label="New" value={data.summary.new} icon={TrendingUp} color="blue" />
            <SummaryCard label="Converted" value={data.summary.converted} icon={Target} color="green" />
            <SummaryCard label="Lost" value={data.summary.lost} icon={AlertTriangle} color="red" />
            <SummaryCard label="Conversion" value={`${data.conversion_rate}%`} icon={BarChart3} color="purple" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniChart title="By Source" data={sourceData} type="pie" />
            <MiniChart title="By Stage" data={stageData} />
          </div>
          {data.by_user?.length > 0 && (
            <DataTable columns={[
              { key: "name", label: "Sales Person" },
              { key: "total", label: "Total" },
              { key: "new", label: "New" },
              { key: "converted", label: "Converted", render: v => <span className="text-green-600 font-semibold">{v || 0}</span> }
            ]} data={data.by_user} />
          )}
          <DataTable columns={[
            { key: "name", label: "Name" },
            { key: "phone", label: "Phone" },
            { key: "source", label: "Source", render: v => v ? <Badge variant="outline">{v}</Badge> : "-" },
            { key: "stage", label: "Stage", render: v => <StatusBadge status={v} /> },
            { key: "assigned_to_name", label: "Assigned To" },
            { key: "created_at", label: "Created", render: v => v ? new Date(v).toLocaleDateString("en-IN") : "-" }
          ]} data={data.leads || []} />
        </>
      )}
    </div>
  );
};

// ─── Attendance Report Tab ────────────────────────
const AttendanceReport = ({ users }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", user_id: "" });

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${BACKEND_URL}/api/reports/attendance?${params}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportData = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("format", "xlsx");
    window.open(`${BACKEND_URL}/api/reports/export/attendance?${params}`, "_blank");
  };

  const chartData = data?.by_user?.map(u => ({ name: u.name?.split(" ")[0] || "?", present: u.present, absent: u.absent, late: u.late })) || [];

  return (
    <div className="space-y-4">
      <DateFilters filters={filters} setFilters={setFilters} users={users}
        extraFilters={
          <button onClick={exportData} className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Download className="w-4 h-4" /> Export
          </button>
        }
      />
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Total Days" value={data.summary.total_days} icon={Calendar} color="indigo" />
            <SummaryCard label="Present" value={data.summary.present} icon={UserCheck} color="green" />
            <SummaryCard label="Absent" value={data.summary.absent} icon={AlertTriangle} color="red" />
            <SummaryCard label="Half Day" value={data.summary.half_day} icon={Clock} color="orange" />
            <SummaryCard label="Late" value={data.summary.late} icon={Clock} color="yellow" />
          </div>
          {chartData.length > 0 && (
            <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-slate-900">Employee Attendance</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#64748B" />
                    <Tooltip contentStyle={{ backgroundColor: "#1E293B", border: "none", borderRadius: "0.75rem", color: "#fff" }} />
                    <Legend />
                    <Bar dataKey="present" fill="#22C55E" radius={[4, 4, 0, 0]} name="Present" />
                    <Bar dataKey="absent" fill="#EF4444" radius={[4, 4, 0, 0]} name="Absent" />
                    <Bar dataKey="late" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Late" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          {data.by_user?.length > 0 && (
            <DataTable columns={[
              { key: "name", label: "Employee" },
              { key: "present", label: "Present", render: v => <span className="text-green-600 font-semibold">{v || 0}</span> },
              { key: "absent", label: "Absent", render: v => <span className="text-red-600 font-semibold">{v || 0}</span> },
              { key: "half_day", label: "Half Day" },
              { key: "late", label: "Late", render: v => v > 0 ? <span className="text-amber-600 font-semibold">{v}</span> : "0" },
              { key: "total_hours", label: "Total Hours", render: v => v ? `${v.toFixed(1)}h` : "-" }
            ]} data={data.by_user} />
          )}
          <DataTable columns={[
            { key: "user_name", label: "Employee" },
            { key: "date", label: "Date" },
            { key: "status", label: "Status", render: v => <StatusBadge status={v} /> },
            { key: "punch_in_time_display", label: "Punch In" },
            { key: "punch_out_time_display", label: "Punch Out" },
            { key: "duration_hours", label: "Hours", render: v => v ? `${Number(v).toFixed(1)}h` : "-" }
          ]} data={data.records || []} />
        </>
      )}
    </div>
  );
};

// ─── Follow-up Report Tab ─────────────────────────
const FollowUpReport = ({ users }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", user_id: "", status: "" });

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${BACKEND_URL}/api/reports/followups?${params}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportData = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("format", "xlsx");
    window.open(`${BACKEND_URL}/api/reports/export/followups?${params}`, "_blank");
  };

  const typeData = data?.by_type ? Object.entries(data.by_type).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="space-y-4">
      <DateFilters filters={filters} setFilters={setFilters} users={users}
        extraFilters={
          <>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none">
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="missed">Missed</option>
            </select>
            <button onClick={exportData} className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download className="w-4 h-4" /> Export
            </button>
          </>
        }
      />
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Total" value={data.summary.total} icon={PhoneCall} color="indigo" />
            <SummaryCard label="Completed" value={data.summary.completed} icon={CheckSquare} color="green" />
            <SummaryCard label="Pending" value={data.summary.pending} icon={Clock} color="yellow" />
            <SummaryCard label="Missed" value={data.summary.missed} icon={AlertTriangle} color="red" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniChart title="By Type" data={typeData} type="pie" />
            {data.by_user?.length > 0 && (
              <DataTable columns={[
                { key: "name", label: "User" },
                { key: "total", label: "Total" },
                { key: "completed", label: "Done", render: v => <span className="text-green-600 font-semibold">{v || 0}</span> },
                { key: "missed", label: "Missed", render: v => v > 0 ? <span className="text-red-600 font-semibold">{v}</span> : "0" }
              ]} data={data.by_user} />
            )}
          </div>
          <DataTable columns={[
            { key: "lead_name", label: "Lead" },
            { key: "type", label: "Type", render: v => {
              const icons = { call: Phone, meeting: Calendar, follow_up: PhoneCall, email: Mail, whatsapp: PhoneCall };
              const I = icons[v] || PhoneCall;
              return <span className="flex items-center gap-1"><I className="w-3.5 h-3.5" />{v || "-"}</span>;
            }},
            { key: "status", label: "Status", render: v => <StatusBadge status={v} /> },
            { key: "date", label: "Date", render: v => v ? new Date(v).toLocaleDateString("en-IN") : "-" },
            { key: "assigned_to_name", label: "Assigned To" },
            { key: "phone", label: "Phone" }
          ]} data={data.followups || []} />
        </>
      )}
    </div>
  );
};

// ─── Main Reports Page ────────────────────────────
const Reports = () => {
  const { selectedBusinessId } = useBusiness();
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("leads");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const url = selectedBusinessId
          ? `${BACKEND_URL}/api/users?business_id=${selectedBusinessId}`
          : `${BACKEND_URL}/api/users`;
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const all = Array.isArray(data) ? data : (data.data || data.users || []);
          // Hard filter client-side as well, in case API ignores the param for non-super_admin
          setUsers(selectedBusinessId ? all.filter(u => u.business_id === selectedBusinessId) : all);
        }
      } catch (e) { console.error(e); }
    };
    fetchUsers();
  }, [selectedBusinessId]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">Reports</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Generate date-wise reports for tasks, leads, attendance & follow-ups</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="leads" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-6 py-2 text-sm font-medium">
            <Users className="w-4 h-4 mr-2" /> Leads
          </TabsTrigger>
          <TabsTrigger value="attendance" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-6 py-2 text-sm font-medium">
            <UserCheck className="w-4 h-4 mr-2" /> Attendance
          </TabsTrigger>
          <TabsTrigger value="followups" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-6 py-2 text-sm font-medium">
            <PhoneCall className="w-4 h-4 mr-2" /> Follow-ups
          </TabsTrigger>
          <TabsTrigger value="performance" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-6 py-2 text-sm font-medium">
            <Activity className="w-4 h-4 mr-2" /> Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads"><LeadReport users={users} /></TabsContent>
        <TabsContent value="attendance"><AttendanceReport users={users} /></TabsContent>
        <TabsContent value="followups"><FollowUpReport users={users} /></TabsContent>
        <TabsContent value="performance"><EmployeePerformanceReport users={users} /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ---------------- Employee Performance ---------------- */

const GRADE_COLORS = {
  "A+": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "A": "bg-green-100 text-green-700 border-green-200",
  "B": "bg-amber-100 text-amber-700 border-amber-200",
  "C": "bg-orange-100 text-orange-700 border-orange-200",
  "D": "bg-rose-100 text-rose-700 border-rose-200",
};

const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; };
const today = () => new Date().toISOString().split("T")[0];

const ymToMonthInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthInputToRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  return { start: start.toISOString().split("T")[0], end: end.toISOString().split("T")[0] };
};

const scoreColor = (score) => {
  if (score >= 90) return "bg-emerald-500 text-white";
  if (score >= 75) return "bg-emerald-300 text-emerald-900";
  if (score >= 60) return "bg-amber-300 text-amber-900";
  if (score >= 40) return "bg-orange-400 text-white";
  if (score > 0) return "bg-rose-500 text-white";
  return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500";
};

const performanceLabel = (score) => {
  if (score >= 90) return { text: "Excellent", cls: "text-emerald-700 bg-emerald-100" };
  if (score >= 75) return { text: "Good", cls: "text-emerald-700 bg-emerald-50" };
  if (score >= 60) return { text: "Average", cls: "text-amber-700 bg-amber-50" };
  if (score >= 40) return { text: "Low", cls: "text-orange-700 bg-orange-50" };
  return { text: "Poor", cls: "text-rose-700 bg-rose-50" };
};

const statusDot = (status, isLate) => {
  if (status === "absent") return "🔴";
  if (status === "half_day") return "🟡";
  if (status === "leave") return "🟣";
  if (status === "present") return isLate ? "🟢" : "🟢";
  return "⚪";
};

const lateColor = (mins) => {
  if (mins <= 0) return "#22C55E";
  if (mins <= 10) return "#22C55E";
  if (mins <= 30) return "#F59E0B";
  return "#EF4444";
};

const isWorkingDay = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.getUTCDay() !== 0;
};

const DailyPerformanceTable = ({ daily, weights }) => {
  const [open, setOpen] = useState({});
  const toggle = (date) => setOpen(o => ({ ...o, [date]: !o[date] }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-slate-500 border-b border-slate-100">
            <th className="text-left py-2 px-3 w-6"></th>
            <th className="text-left py-2 px-3">Date</th>
            <th className="text-left py-2 px-3">Attendance</th>
            <th className="text-left py-2 px-3">Late By</th>
            <th className="text-right py-2 px-3">Assigned</th>
            <th className="text-right py-2 px-3">Completed</th>
            <th className="text-left py-2 px-3">Performance</th>
          </tr>
        </thead>
        <tbody>
          {daily.filter(d => isWorkingDay(d.date) || d.status === "present" || d.status === "half_day" || d.status === "leave").map(d => {
            const date = new Date(d.date + "T00:00:00Z");
            const label = `${String(date.getUTCDate()).padStart(2, "0")} ${date.toLocaleString("en-US", { month: "short", timeZone: "UTC" })}`;
            const combined = Math.round((d.attendance_score * weights.attendance / 100) + (d.task_score * weights.tasks / 100));
            const perf = performanceLabel(combined);
            const statusText = d.status === "half_day" ? "Half Day" : d.status === "leave" ? "Leave" : d.status === "absent" ? "Absent" : "Present";
            const lateText = d.status === "absent" ? "-" : d.is_late ? `${d.late_minutes} Min` : "On Time";
            const canExpand = (d.completed_task_titles || []).length > 0;
            return (
              <Fragment key={d.date}>
                <tr
                  onClick={() => canExpand && toggle(d.date)}
                  className={`border-b border-slate-50 ${canExpand ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" : ""}`}
                >
                  <td className="py-2 px-3 text-slate-400">
                    {canExpand ? (open[d.date] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                  </td>
                  <td className="py-2 px-3 text-slate-800 dark:text-slate-100">{label}</td>
                  <td className="py-2 px-3 capitalize">{statusText}</td>
                  <td className="py-2 px-3 text-slate-500">{lateText}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{d.tasks_assigned}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{d.tasks_completed}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${perf.cls}`}>{perf.text}</span>
                  </td>
                </tr>
                {open[d.date] && canExpand && (
                  <tr className="bg-slate-50/60 dark:bg-slate-800/40">
                    <td></td>
                    <td colSpan={6} className="py-2 px-3">
                      <div className="text-xs text-slate-500 mb-1.5">Completed tasks on {label}:</div>
                      <ul className="text-sm space-y-1">
                        {d.completed_task_titles.map(t => (
                          <li key={t.task_id} className="flex items-start gap-2">
                            <CheckSquare className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-700 dark:text-slate-200">{t.title}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PerfTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const date = new Date(d.date + "T00:00:00Z");
  const dateLabel = `${date.getUTCDate()} ${date.toLocaleString("en-US", { month: "short", timeZone: "UTC" })}`;
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 text-xs text-slate-700 dark:text-slate-200 space-y-1">
      <div className="font-semibold text-slate-900 dark:text-white">Date: {dateLabel}</div>
      <div>Attendance: <span className="capitalize">{d.status.replace("_", " ")}</span></div>
      {d.is_late && <div>Late By: {d.late_minutes} Minutes</div>}
      <div>Tasks Assigned: {d.tasks_assigned}</div>
      <div>Tasks Completed: {d.tasks_completed}</div>
      <div>Performance Score: {d.combined}%</div>
    </div>
  );
};

const EmployeePerformanceReport = ({ users }) => {
  const { selectedBusinessId } = useBusiness();
  const employees = (users || []).filter(u => u.role !== "super_admin");
  const [filters, setFilters] = useState({
    user_id: "",
    department: "",
    start_date: monthStart(),
    end_date: today(),
    metric: "combined",
  });
  const [month, setMonth] = useState(ymToMonthInput(new Date()));
  const [detail, setDetail] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [weights, setWeights] = useState({ attendance: 40, tasks: 60 });
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);

  const departments = Array.from(new Set(employees.map(u => u.department).filter(Boolean))).sort();
  const filteredEmployees = filters.department
    ? employees.filter(u => (u.department || "") === filters.department)
    : employees;

  useEffect(() => {
    if (employees.length && !filters.user_id) {
      setFilters(f => ({ ...f, user_id: employees[0].user_id }));
    }
  }, [employees, filters.user_id]);

  const load = useCallback(async () => {
    if (!filters.user_id || !filters.start_date || !filters.end_date) return;
    setLoading(true);
    try {
      const baseParams = {
        user_id: filters.user_id,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };
      if (selectedBusinessId) baseParams.business_id = selectedBusinessId;
      const qs = new URLSearchParams(baseParams).toString();
      const rankParams = new URLSearchParams({
        start_date: filters.start_date,
        end_date: filters.end_date,
        ...(selectedBusinessId ? { business_id: selectedBusinessId } : {}),
      }).toString();
      const [dRes, rRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/reports/employee-performance?${qs}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/reports/employee-performance/ranking?${rankParams}`, { credentials: "include" }),
      ]);
      if (dRes.ok) {
        const d = await dRes.json();
        setDetail(d);
        if (d.weights) setWeights(d.weights);
        setInsights(d.insights || []);
      }
      if (rRes.ok) {
        const r = await rRes.json();
        setRanking(r.ranking || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters, selectedBusinessId]);

  useEffect(() => { load(); }, [load]);

  const s = detail?.summary || {};
  const dailyChart = (detail?.daily || []).map(d => {
    const day = parseInt(d.date.split("-")[2], 10);
    const combined = (d.attendance_score * weights.attendance / 100) + (d.task_score * weights.tasks / 100);
    return {
      day, date: d.date,
      attendance: d.attendance_score,
      tasks: Math.round(d.task_score),
      tasks_assigned: d.tasks_assigned,
      tasks_completed: d.tasks_completed,
      status: d.status,
      is_late: d.is_late,
      late_minutes: d.late_minutes,
      combined: Math.round(combined),
    };
  });

  const lateChart = dailyChart.filter(d => d.status !== "off");

  const myRank = ranking.find(r => r.user_id === filters.user_id);

  const onMonthChange = (ym) => {
    setMonth(ym);
    const { start, end } = monthInputToRange(ym);
    setFilters(f => ({ ...f, start_date: start, end_date: end }));
  };

  const buildRows = () => {
    const header = ["Date", "Status", "Attendance Score", "Tasks Assigned", "Tasks Completed", "Task Score", "Combined Score"];
    const rows = (detail?.daily || []).map(d => {
      const combined = Math.round((d.attendance_score * weights.attendance / 100) + (d.task_score * weights.tasks / 100));
      return [d.date, d.status, d.attendance_score, d.tasks_assigned, d.tasks_completed, Math.round(d.task_score), combined];
    });
    return { header, rows };
  };

  const downloadBlob = (data, mime, ext) => {
    const empName = (detail?.user?.name || "employee").trim();
    const blob = new Blob([data], { type: mime });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `performance_${empName}_${filters.start_date}_to_${filters.end_date}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCsv = () => {
    if (!detail) return;
    const { header, rows } = buildRows();
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    downloadBlob(csv, "text/csv;charset=utf-8;", "csv");
  };

  const exportExcel = () => {
    if (!detail) return;
    const { header, rows } = buildRows();
    const empName = detail.user?.name || "Employee";
    const sumPairs = Object.entries(detail.summary || {}).map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join("");
    const dataRows = rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="utf-8"/><title>Performance</title></head>
<body>
<h3>Employee Performance — ${empName}</h3>
<p>${filters.start_date} to ${filters.end_date} · Grade ${detail.summary?.grade || ""}</p>
<table border="1"><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>${sumPairs}</tbody></table>
<br/>
<table border="1"><thead><tr>${header.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${dataRows}</tbody></table>
</body></html>`;
    downloadBlob(html, "application/vnd.ms-excel;charset=utf-8;", "xls");
  };

  const exportPdf = () => {
    if (!detail) return;
    const { header, rows } = buildRows();
    const empName = detail.user?.name || "Employee";
    const sumCards = Object.entries(detail.summary || {}).map(([k, v]) =>
      `<div class="card"><div class="label">${k.replace(/_/g, " ")}</div><div class="value">${v}</div></div>`
    ).join("");
    const dataRows = rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("");
    const insightsList = (insights || []).map(i => `<li>${i.text}</li>`).join("");
    const rankBlock = ranking.slice(0, 10).map(r => `<tr><td>#${r.rank}</td><td>${r.name}</td><td>${r.attendance_pct}%</td><td>${r.task_completion_pct}%</td><td>${r.productivity_score}</td><td>${r.grade}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
<title>Performance Report — ${empName}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;padding:24px;max-width:900px;margin:auto}
  h1{font-size:22px;margin:0 0 4px 0}
  .meta{color:#64748b;font-size:13px;margin-bottom:18px}
  .cards{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:18px}
  .card{border:1px solid #e2e8f0;border-radius:8px;padding:10px}
  .label{font-size:10px;color:#64748b;text-transform:uppercase}
  .value{font-size:18px;font-weight:600;margin-top:4px}
  h2{font-size:14px;margin:20px 0 8px;color:#334155}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}
  th{background:#f1f5f9}
  ul{padding-left:18px}
  @media print{ button{display:none} }
</style></head><body>
<h1>Employee Performance — ${empName}</h1>
<div class="meta">${filters.start_date} to ${filters.end_date} · Grade ${detail.summary?.grade || ""}</div>
<button onclick="window.print()" style="margin-bottom:12px;padding:6px 12px;border:1px solid #ccc;border-radius:6px;cursor:pointer">Save as PDF</button>
<h2>Summary</h2>
<div class="cards">${sumCards}</div>
${insightsList ? `<h2>Insights</h2><ul>${insightsList}</ul>` : ""}
${rankBlock ? `<h2>Team Ranking (Top 10)</h2><table><thead><tr><th>Rank</th><th>Employee</th><th>Attendance %</th><th>Task %</th><th>Score</th><th>Grade</th></tr></thead><tbody>${rankBlock}</tbody></table>` : ""}
<h2>Daily Performance</h2>
<table><thead><tr>${header.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${dataRows}</tbody></table>
<script>window.onload=()=>{setTimeout(()=>window.print(),250)}</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const setDepartment = async (userId, currentDept) => {
    const dept = prompt("Department (blank to clear):", currentDept || "");
    if (dept === null) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ department: dept.trim() || null }),
      });
      if (res.ok) load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6 mt-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 p-4 dark:bg-slate-900 dark:border-slate-700">
        {departments.length > 0 && (
          <select
            value={filters.department}
            onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          >
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <select
          value={filters.user_id}
          onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
        >
          {filteredEmployees.map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
        </select>
        <input type="month" value={month} onChange={e => onMonthChange(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
        <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
        <div className="flex items-center gap-1.5">
          <button onClick={exportCsv} disabled={!detail}
            className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={exportExcel} disabled={!detail}
            className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportPdf} disabled={!detail}
            className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:hover:bg-slate-700">
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
        <div className="ml-auto text-xs text-slate-500">
          Weights: Attendance {Math.round(weights.attendance)}% · Tasks {Math.round(weights.tasks)}%
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
        <SummaryCard label="Working Days" value={s.working_days ?? 0} icon={Calendar} color="indigo" />
        <SummaryCard label="Present Days" value={s.days_present ?? 0} icon={UserCheck} color="emerald" />
        <SummaryCard label="Half Days" value={s.half_days ?? 0} icon={Hourglass} color="amber" />
        <SummaryCard label="Leave Days" value={s.leave_days ?? 0} icon={CalendarDays} color="purple" />
        <SummaryCard label="Absent Days" value={s.absent_days ?? 0} icon={XCircle} color="rose" />
        <SummaryCard label="Total Late Days" value={s.total_late_days ?? 0} icon={AlarmClock} color="orange" />
        <SummaryCard label="Avg Late Minutes" value={s.avg_late_minutes ?? 0} icon={Clock} color="orange" />
        <SummaryCard label="Tasks Assigned" value={s.tasks_assigned ?? 0} icon={ClipboardList} color="amber" />
        <SummaryCard label="Tasks Completed" value={s.tasks_completed ?? 0} icon={CheckSquare} color="emerald" />
        <SummaryCard label="Task Completion %" value={`${s.task_completion_pct ?? 0}%`} icon={Target} color="indigo" />
        <SummaryCard label="Performance Score" value={s.productivity_score ?? 0} icon={Activity} color="purple" />
        <SummaryCard
          label="Rank · Grade"
          value={myRank ? `#${myRank.rank} · ${myRank.grade}` : (s.grade || "-")}
          icon={Trophy}
          color="amber"
        />
      </div>

      {/* Late Arrival Trend */}
      {lateChart.length > 0 && (
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <AlarmClock className="w-4 h-4 text-orange-500" /> Late Arrival Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={lateChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: "Min Late", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b" } }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  if (d.status === "absent") return <div className="bg-white border rounded p-2 text-xs">{d.day} · Absent</div>;
                  return <div className="bg-white border rounded p-2 text-xs">{d.day} · {d.late_minutes} min late</div>;
                }} />
                <Bar dataKey="late_minutes" radius={[4, 4, 0, 0]}>
                  {lateChart.map((d, i) => <Cell key={i} fill={d.status === "absent" ? "#94A3B8" : lateColor(d.late_minutes)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" />0–10 min</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" />11–30 min</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500" />31+ min</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-400" />Absent</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Tasks Completed */}
      <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-emerald-600" /> Daily Tasks Completed
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyChart.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-slate-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                  formatter={(v) => [`${v} task${v === 1 ? "" : "s"}`, "Completed"]}
                  labelFormatter={(d) => `Day ${d}`}
                />
                <Bar dataKey="tasks_completed" name="Tasks Completed" fill="#22C55E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Monthly Performance Timeline */}
      <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Activity className="w-4 h-4 text-indigo-600" /> Monthly Performance Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyChart.length === 0 ? (
            <div className="text-sm text-slate-400">No data</div>
          ) : (
            <div className="relative pl-6 max-h-96 overflow-y-auto">
              <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-slate-200 dark:bg-slate-700" />
              {detail.daily.filter(d => isWorkingDay(d.date) || d.status === "present" || d.status === "half_day" || d.status === "leave").map(d => {
                const date = new Date(d.date + "T00:00:00Z");
                const label = `${String(date.getUTCDate()).padStart(2, "0")} ${date.toLocaleString("en-US", { month: "short", timeZone: "UTC" })}`;
                const lateBit = d.is_late ? ` (${d.late_minutes} Min Late)` : (d.status === "present" ? " (On Time)" : "");
                const statusText = d.status === "half_day" ? "Half Day"
                  : d.status === "leave" ? "Leave"
                  : d.status === "absent" ? "Absent"
                  : "Present";
                return (
                  <div key={d.date} className="relative pb-3">
                    <div className={`absolute -left-[18px] top-1 w-3 h-3 rounded-full ${d.status === "absent" ? "bg-rose-500" : d.status === "half_day" ? "bg-amber-500" : d.status === "leave" ? "bg-violet-500" : d.is_late ? "bg-orange-500" : "bg-emerald-500"}`} />
                    <div className="text-sm text-slate-700 dark:text-slate-200">
                      <span className="font-medium">{label}</span>
                      <span className="text-slate-500"> → </span>
                      <span>{statusText}{lateBit}</span>
                      {d.status !== "absent" && d.tasks_completed > 0 && (
                        <span className="text-slate-500"> → {d.tasks_completed} Task{d.tasks_completed === 1 ? "" : "s"} Completed</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Performance Table */}
      <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-600" /> Daily Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyChart.length === 0 ? (
            <div className="text-sm text-slate-400">No data</div>
          ) : (
            <DailyPerformanceTable daily={detail.daily} weights={weights} />
          )}
        </CardContent>
      </Card>

      {/* Manager Insights */}
      {insights.length > 0 && (
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" /> Manager Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {insights.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className={`mt-1 inline-block w-2 h-2 rounded-full ${i.type === "positive" ? "bg-emerald-500" : i.type === "negative" ? "bg-rose-500" : "bg-indigo-500"}`} />
                  <span className="text-slate-700 dark:text-slate-200">{i.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Recent Tasks (drill-down) */}
      {detail?.recent_tasks?.length > 0 && (
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-indigo-600" /> Recent Tasks — {detail.user?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b border-slate-100">
                    <th className="text-left py-2 px-3">Title</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Due</th>
                    <th className="text-left py-2 px-3">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recent_tasks.map(t => (
                    <tr key={t.task_id} className="border-b border-slate-50">
                      <td className="py-2 px-3 text-slate-800 dark:text-slate-100">{t.title}</td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${t.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{t.status}</span>
                      </td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{t.due_date ? t.due_date.slice(0, 10) : "-"}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{t.completed_at ? t.completed_at.slice(0, 10) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking table */}
      <Card className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-900 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" /> Employee Ranking
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ranking.length === 0 ? (
            <div className="text-slate-400 text-sm">No employees to rank yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b border-slate-100">
                    <th className="text-left py-2 px-3">Rank</th>
                    <th className="text-left py-2 px-3">Employee</th>
                    <th className="text-left py-2 px-3">Department</th>
                    <th className="text-right py-2 px-3">Attendance %</th>
                    <th className="text-right py-2 px-3">Task %</th>
                    <th className="text-right py-2 px-3">Score</th>
                    <th className="text-center py-2 px-3">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map(r => (
                    <tr
                      key={r.user_id}
                      onClick={() => setFilters(f => ({ ...f, user_id: r.user_id }))}
                      className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${r.user_id === filters.user_id ? "bg-indigo-50/40 dark:bg-indigo-900/20" : ""}`}
                    >
                      <td className="py-2.5 px-3 font-semibold">
                        {r.rank === 1 ? <span title="Gold">🥇</span>
                          : r.rank === 2 ? <span title="Silver">🥈</span>
                          : r.rank === 3 ? <span title="Bronze">🥉</span>
                          : `#${r.rank}`}
                      </td>
                      <td className="py-2.5 px-3 text-slate-800 dark:text-slate-100">{r.name || r.email}</td>
                      <td className="py-2.5 px-3 text-slate-500 text-xs">
                        <button
                          onClick={(e) => { e.stopPropagation(); setDepartment(r.user_id, r.department); }}
                          className="hover:text-indigo-600 underline-offset-2 hover:underline"
                          title="Click to set"
                        >{r.department || "— set —"}</button>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{r.attendance_pct}%</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{r.task_completion_pct}%</td>
                      <td className="py-2.5 px-3 text-right font-semibold tabular-nums">{r.productivity_score}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${GRADE_COLORS[r.grade] || ""}`}>{r.grade}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
